import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { History, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import type { UserSession } from "@/types/session";
import { ChatInput } from "./ChatInput";
import { useAuth } from "@/hooks/useAuth";
import { deriveWorkspacePersona } from "@/config/workspacePersona";
import { getHubCommandHints, getHubHeroChips } from "@/lib/sidebarDestinationRegistry";

export const HubHero = ({
  greetingLine,
  session,
  isLoading,
  isDisabled,
  onSubmit,
  onVoiceClick,
  onFilesSelected,
}: {
  greetingLine: string;
  session: UserSession | null;
  isLoading: boolean;
  isDisabled: boolean;
  onSubmit: (message: string, session: UserSession | null | undefined) => void;
  onVoiceClick?: () => void;
  onFilesSelected?: (files: FileList) => void;
}) => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const persona = deriveWorkspacePersona(userData);

  const visibleChips = useMemo(() => getHubHeroChips(persona), [persona]);
  const commandHints = useMemo(() => getHubCommandHints(persona), [persona]);

  return (
    <div className="flex flex-1 min-h-0 flex-col items-center justify-center gap-6 px-2 py-4 text-center md:gap-8">
      <div className="flex flex-col items-center gap-3">
        <div className="flex items-center justify-center gap-3">
          <Sparkles className="h-6 w-6 text-bodhi-gold md:h-7 md:w-7" aria-hidden />
          <h1 className="font-literata text-3xl font-semibold text-white md:text-5xl">{greetingLine}</h1>
        </div>
        <p className="font-inter text-sm text-white/80 md:text-base">What can we work on together?</p>
      </div>

      <div
        className={cn(
          "w-full max-w-2xl rounded-2xl bg-white shadow-lg ring-1 ring-black/5",
          "px-1 pb-1 pt-1"
        )}
      >
        <ChatInput
          hero
          session={session}
          onSubmit={onSubmit}
          isLoading={isLoading}
          isDisabled={isDisabled}
          onVoiceClick={onVoiceClick}
          onFilesSelected={onFilesSelected}
          chatEntry="ai_mentor"
          placeholder="How can I help you today?"
        />
      </div>

      <p className="max-w-xl px-2 font-inter text-xs text-white/65 md:text-sm">{commandHints}</p>

      <div className="flex flex-wrap items-center justify-center gap-2">
        {visibleChips.map((chip) => {
          const Icon = chip.icon;
          return (
            <button
              key={chip.id}
              type="button"
              onClick={() => chip.onClick(navigate, persona)}
              className={cn(
                "inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-3 py-1.5",
                "font-inter text-xs text-white/90 backdrop-blur-sm transition hover:bg-white/20 md:text-sm"
              )}
            >
              <Icon className="h-3.5 w-3.5 shrink-0 md:h-4 md:w-4" aria-hidden />
              <span>{chip.label}</span>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => navigate("/history")}
        className="inline-flex items-center gap-1.5 font-inter text-xs text-white/70 transition hover:text-white md:text-sm"
      >
        <History className="h-3.5 w-3.5" aria-hidden />
        <span>Full history</span>
      </button>
    </div>
  );
};
